import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TopBar } from "@/components/TopBar";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { searchMessages } from "@/features/search/api";
import { SearchResults } from "@/features/search/SearchResults";
import { AskAIModal } from "@/features/search/AskAIModal";
import { SearchFiltersForm, type SearchFilterState } from "@/features/search/SearchFilters";
import { filtersToOperatorQuery, fromDateInputValue } from "@/features/search/query";
import { useRooms } from "@/lib/rooms";
import { useSession } from "@/lib/auth";
import { apiGet } from "@/lib/api";
import type { User } from "@/lib/types";

function splitParam(value: string | null): string[] {
  return value ? value.split(",").map((part) => part.trim()).filter(Boolean) : [];
}

export function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [draft, setDraft] = useState(params.get("q") || "");
  const [question, setQuestion] = useState("");
  const navigate = useNavigate();
  const session = useSession();
  const { rooms } = useRooms();
  const roomNames = useMemo(() => Object.fromEntries(rooms.map(room => [room.id, room.name])), [rooms]);
  const query = params.get("q") || "";
  useEffect(() => { setDraft(query); }, [query]);
  const roomIds = useMemo(() => {
    const many = splitParam(params.get("rooms"));
    const one = params.get("room");
    return many.length ? many : one ? [one] : [];
  }, [params]);
  const authorIds = useMemo(() => splitParam(params.get("from")), [params]);
  const after = params.get("after") || "";
  const before = params.get("before") || "";
  const hasFile = params.get("has") === "file";
  const [filters, setFilters] = useState<SearchFilterState>({ roomIds, authorIds, after, before, hasFile });
  useEffect(() => { setFilters({ roomIds, authorIds, after, before, hasFile }); }, [roomIds, authorIds, after, before, hasFile]);
  // Only structured controls contain IDs; operator names are resolved by the server.
  const effectiveRooms = filters.roomIds;
  const authorQueries = useQuery({
    queryKey: ["search-authors", session?.tenantId, session?.user.id, effectiveRooms.join(",")],
    enabled: effectiveRooms.length > 0,
    queryFn: async () => {
      const seen = new Map<string, User>();
      for (const id of effectiveRooms) {
        const members = await apiGet<{ members: { user_id: string; display_name?: string; email?: string }[] }>(`/v1/rooms/${encodeURIComponent(id)}/members`);
        for (const member of members.members) {
          if (!seen.has(member.user_id)) seen.set(member.user_id, { id: member.user_id, email: member.email ?? "", displayName: member.display_name || member.user_id });
        }
      }
      return [...seen.values()];
    },
  });
  const authors = authorQueries.data ?? [];
  const channelIds = effectiveRooms.length ? effectiveRooms : undefined;
  const afterMs = fromDateInputValue(filters.after);
  const beforeMs = fromDateInputValue(filters.before);
  const authorsFilter = filters.authorIds;
  const fileFilter = filters.hasFile;
  const canSearch = Boolean(query.trim() || authorsFilter.length || channelIds?.length || afterMs || beforeMs || fileFilter);
  const result = useQuery({
    queryKey: ["search", session?.tenantId, session?.user.id, query, channelIds, authorsFilter, afterMs, beforeMs, fileFilter],
    enabled: canSearch,
    queryFn: () => searchMessages({
      query: query.trim() || filtersToOperatorQuery({ text: "", authorIds: authorsFilter, roomIds: channelIds ?? [], before: beforeMs, after: afterMs, hasFile: fileFilter }),
      mode: "hybrid",
      limit: 50,
      channel_ids: channelIds,
      author_ids: authorsFilter.length ? authorsFilter : undefined,
      has_file: fileFilter || undefined,
      date_range: afterMs || beforeMs ? { after: afterMs, before: beforeMs } : undefined,
    }),
  });
  const apply = () => {
    const next = new URLSearchParams();
    if (draft.trim()) next.set("q", draft.trim());
    if (filters.roomIds.length) next.set("rooms", filters.roomIds.join(","));
    if (filters.authorIds.length) next.set("from", filters.authorIds.join(","));
    if (filters.after) next.set("after", filters.after);
    if (filters.before) next.set("before", filters.before);
    if (filters.hasFile) next.set("has", "file");
    setParams(next);
  };
  const askQuery = filtersToOperatorQuery({
    text: draft.trim(),
    authorIds: authorsFilter,
    roomIds: channelIds ?? [],
    before: beforeMs,
    after: afterMs,
    hasFile: fileFilter,
  });
  return <><TopBar title="検索" /><div className="p-6 space-y-5 overflow-auto">
    <form className="space-y-4" onSubmit={e => { e.preventDefault(); apply(); }}>
      <div className="flex flex-wrap gap-3">
        <Input aria-label="検索キーワード" value={draft} onChange={e => setDraft(e.target.value)} placeholder="メッセージを検索  例: from:me in:開発 has:file" />
        <Button type="submit" disabled={!draft.trim() && !filters.authorIds.length && !filters.roomIds.length && !filters.hasFile && !filters.after && !filters.before}>検索</Button>
        <Button type="button" variant="secondary" disabled={!draft.trim() && !canSearch} onClick={() => { apply(); setQuestion(askQuery || draft.trim()); }}>AI に質問</Button>
      </div>
      <SearchFiltersForm rooms={rooms} authors={authors} value={filters} onChange={setFilters} />
    </form>
    <SearchResults roomNames={roomNames} result={result.data ?? null} loading={result.isFetching} error={result.error?.message} onJump={hit => navigate(`/rooms/${encodeURIComponent(hit.room_id)}?message=${encodeURIComponent(hit.message_id)}`)} />
  </div><AskAIModal roomNames={roomNames} open={!!question} question={question} onClose={() => setQuestion("")} onJumpCitation={cite => { setQuestion(""); navigate(`/rooms/${encodeURIComponent(cite.room_id)}?message=${encodeURIComponent(cite.message_id)}`); }} /></>;
}
